import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"
import { IMask } from "react-imask"
import { TableVirtuoso } from "react-virtuoso"
import { TableBadge } from "@/components/table-badge"
import { formatDate } from "@/lib/format-date"
import { joinStrings } from "@/lib/joinString"
import { cn, getCleanPhoneNumber } from "@/lib/utils"
import { GenderIcon } from "@/modules/patients/components/gender-icon"
import { PatientTableActionMenu } from "@/modules/patients/components/patient-table-action-menu"
import PatientEmpty from "@/modules/patients/components/patient-table-empty"
import { usePhoneNumberCountryList } from "@/modules/patients/components/phoneNumber/hooks/usePhoneNumberCountryList"
import { useOpenDePatientInboundCallV2 } from "@/modules/patients/hooks/useOpenDePatientInboundCallV2"
import { useOpenPatientInboundCall } from "@/modules/patients/hooks/useOpenPatientInboundCall"
import { HighlightedText } from "@/modules/patients/patient-list/highlighted-text"
import { PatientCoverageCell } from "@/modules/patients/patient-list/patient-coverage-cell"
import { PatientSearchItem } from "@/modules/patients/patient-list/types/PatientSearchItem"
import { SofiaEnvironment } from "@/sofia-environment"
import useCallContextStore from "@/stores/useCallContextStore"

interface PatientTableProps extends React.HTMLAttributes<HTMLDivElement> {
  data: PatientSearchItem[]
  keyword: string
  hasFilterChanged: boolean
  totalCount: number
  isHealthInsuranceCardNumberMissing: boolean
  endReached?: () => void
  setPatientQuery: Dispatch<SetStateAction<string>>
  onPatientUpdated: () => void
}

const columns = [
  { accessorKey: "name", title: "patient.table.patient", width: 260, visible: true },
  { accessorKey: "dateOfBirth", title: "patient.table.dateOfbirth", width: 100, visible: true },
  { accessorKey: "phoneNumber", title: "patient.table.phoneNumber", width: 160, visible: true },
  { accessorKey: "address", title: "patient.table.address", visible: true },
  { accessorKey: "compulsoryOrganisationName", title: "patient.table.compulsoryInsurance", width: 200, visible: true },
  {
    accessorKey: "supplementary1OrganisationName",
    title: "patient.table.supplementaryInsurance",
    width: 200,
    visible: true,
  },
  {
    accessorKey: "supplementary2OrganisationName",
    title: "patient.table.supplementaryInsurance",
    width: 200,
    visible: true,
  },
  {
    accessorKey: "supplementary3OrganisationName",
    title: "patient.table.supplementaryInsurance",
    width: 200,
    visible: true,
  },
  { accessorKey: "lastContact", title: "patient.table.lastContact", width: 100, visible: true },
  {
    accessorKey: "searchScore",
    title: "patient.table.searchScore",
    width: 100,
    visible: SofiaEnvironment.AllowDebugFeatures,
  },
  { accessorKey: "action", title: "", width: 48, visible: true },
]

const PatientTable = ({
  keyword,
  data,
  hasFilterChanged,
  totalCount,
  isHealthInsuranceCardNumberMissing,
  endReached,
  setPatientQuery,
  onPatientUpdated,
  ...props
}: PatientTableProps) => {
  const { t } = useTranslation()
  const [isAtTop, setIsAtTop] = useState(true)

  const callerPhoneNumber = useCallContextStore((state) => state.callerPhoneNumber)
  const callId = useCallContextStore((state) => state.callID)
  const { getCountryByPhoneNumber } = usePhoneNumberCountryList("")

  const { openPatientInboundCall, patientInboundCallDialog } = useOpenPatientInboundCall(setPatientQuery)
  const { openDePatientInboundCallV2, dePatientInboundCallDialogV2 } = useOpenDePatientInboundCallV2(setPatientQuery)

  const onRowClick = (patient: PatientSearchItem) => {
    const patientId = patient?.patientId
    const country = patient?.country

    if (country && patientId) {
      if (callId) {
        openPatientInboundCall(patientId, joinStrings([patient.firstName, patient.lastName], " "))
      } else {
        window.open(`/patients/${country}/${patientId}`, patientId)
      }
    }
  }

  if (!data || data.length === 0) {
    const noPatient = keyword || callerPhoneNumber

    return (
      <PatientEmpty
        title={noPatient ? t("patient.table.noPatientFound") : t("patient.table.notYetSearching")}
        message={
          noPatient ? t("patient.table.noPatientFoundDescription") : t("patient.table.notYetSearchingDescription")
        }
      />
    )
  }

  return (
    <div className="flex-1" {...props}>
      <div className="flex h-full flex-col px-8">
        {data.length && (
          <div className="bg-body-subdued sticky top-0 z-1 flex h-10 items-center gap-2 pb-2">
            <div className="text-base font-medium">
              {callerPhoneNumber && !hasFilterChanged
                ? t("patient.table.onCallTableHeader")
                : t("patient.table.patients")}
            </div>
            <TableBadge value={totalCount} />
          </div>
        )}
        <TableVirtuoso
          className="patient-table"
          data={data}
          increaseViewportBy={300}
          atTopStateChange={setIsAtTop}
          endReached={endReached}
          components={{
            Table: ({ ...props }) => {
              return (
                <table {...props} className="w-full min-w-425 table-fixed border-separate border-spacing-0 pb-3">
                  {props.children}
                </table>
              )
            },
            TableHead: (props) => (
              <thead
                {...props}
                className={cn(
                  "text-on-surface-subdued px-4 py-2 text-xs whitespace-nowrap",
                  "after:bg-background-subdued after:absolute after:top-0 after:left-0 after:-z-10 after:h-full after:w-full",
                  !isAtTop && "table-header-shadow",
                )}
              />
            ),
            TableRow: (props) => (
              <tr
                {...props}
                data-testid="table-row"
                className="group bg-surface text-on-surface relative cursor-pointer border-b text-sm"
                onClick={() => onRowClick(props.item)}></tr>
            ),
          }}
          itemContent={(_, patient) => {
            const { highlights } = patient

            const country = getCountryByPhoneNumber(patient.phoneNumber ?? "")
            let formattedPhoneNumber = getCleanPhoneNumber(patient.phoneNumber)

            if (country?.mask && patient.phoneNumber) {
              const masked = IMask.createMask({ mask: country.mask })
              masked.resolve(patient.phoneNumber)
              formattedPhoneNumber = masked.value
            }

            return (
              <>
                <td
                  className={cn(
                    "group-hover:bg-surface-hovered border-b px-2 transition-all duration-300",
                    "first-of-type:border-l first-of-type:pl-4 first-of-type:group-last-of-type:rounded-bl-xl last-of-type:border-r last-of-type:group-last-of-type:rounded-br-xl",
                  )}>
                  <div className="flex items-center gap-2">
                    <div className="size-6">
                      <GenderIcon title={patient.gender} gender={patient.gender} className="size-6" size={20} />
                    </div>
                    <div className="truncate">
                      <HighlightedText
                        className="truncate"
                        highlights={highlights}
                        fields={[
                          { key: "lastName", plain: patient.lastName ? patient.lastName + ", " : null },
                          { key: "firstName", plain: patient.firstName },
                        ]}
                      />
                      <HighlightedText
                        className="text-on-surface-subdued text-xs leading-snug"
                        highlights={highlights}
                        fields={[{ key: "internalId", plain: `${patient.displayId ?? ""}` }]}
                      />
                    </div>
                  </div>
                </td>
                <td className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300">
                  <div className="flex items-center gap-4">
                    <p>{formatDate(new Date(patient.birthDate))}</p>
                  </div>
                </td>
                <td
                  className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300"
                  data-testid="patient-phone-number">
                  {formattedPhoneNumber}
                </td>
                <td className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300">
                  <HighlightedText
                    className="truncate"
                    highlights={highlights}
                    fields={[
                      { key: "street", plain: patient.street },
                      { key: "postalCode", plain: patient.postalCode },
                      { key: "city", plain: patient.city },
                    ]}
                    joinWith=", "
                  />
                </td>
                <td className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300">
                  <PatientCoverageCell
                    patient={patient}
                    coverageSlot="compulsory"
                    isHealthInsuranceCardNumberMissing={isHealthInsuranceCardNumberMissing}
                  />
                </td>
                <td className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300">
                  <PatientCoverageCell
                    patient={patient}
                    coverageSlot="supplementary1"
                    isHealthInsuranceCardNumberMissing={isHealthInsuranceCardNumberMissing}
                  />
                </td>
                <td className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300">
                  <PatientCoverageCell
                    patient={patient}
                    coverageSlot="supplementary2"
                    isHealthInsuranceCardNumberMissing={isHealthInsuranceCardNumberMissing}
                  />
                </td>
                <td className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300">
                  <PatientCoverageCell
                    patient={patient}
                    coverageSlot="supplementary3"
                    isHealthInsuranceCardNumberMissing={isHealthInsuranceCardNumberMissing}
                  />
                </td>
                <td className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300">
                  <p>{patient.lastContactAt && formatDate(new Date(patient.lastContactAt))}</p>
                </td>
                {SofiaEnvironment.AllowDebugFeatures && (
                  <td className="group-hover:bg-surface-hovered border-b px-2 transition-all duration-300">
                    {patient.searchScore && <p>{patient.searchScore.toFixed(2)}</p>}
                  </td>
                )}
                <td
                  className={cn(
                    "group-hover:bg-surface-hovered border-b px-2 transition-all duration-300",
                    "first-of-type:group-last-of-type:rounded-bl-xl last-of-type:border-r last-of-type:group-last-of-type:rounded-br-xl",
                  )}>
                  <PatientTableActionMenu
                    data={patient}
                    openPatientInboundCall={openPatientInboundCall}
                    openDePatientInboundCallV2={openDePatientInboundCallV2}
                    onPatientUpdated={onPatientUpdated}
                  />
                </td>
              </>
            )
          }}
          fixedHeaderContent={() => (
            <tr className="bg-surface">
              {columns.map((header) => {
                return (
                  header.visible && (
                    <th
                      key={header.accessorKey}
                      className={cn(
                        "border-border border-t border-b px-2 py-2.5 text-left font-normal",
                        "first-of-type:rounded-tl-xl first-of-type:border-l first-of-type:pl-4 last-of-type:rounded-tr-xl last-of-type:border-r",
                      )}
                      style={{ width: header.width ? `${header.width}px` : "auto" }}>
                      {t(header.title)}
                    </th>
                  )
                )
              })}
            </tr>
          )}
        />
      </div>

      {patientInboundCallDialog}
      {dePatientInboundCallDialogV2}
    </div>
  )
}

export { PatientTable }
